import { useT } from "@/i18n/LanguageProvider";

/**
 * QualitySeal – a slowly-rotating circular "premium quality" stamp
 * placed in the hero corner. Editorial / champagne-label aesthetic:
 *   – outer rotating ring with the four key brand promises
 *   – center: brand monogram + "Effizienzhaus 40 · Neubau 2026"
 *
 * Sizes:    desktop ~ 156 px · tablet ~ 124 px · mobile ~ 96 px
 * Position: parent must be `relative`; component is `absolute`.
 */
export const QualitySeal = () => {
    const t = useT();

    // Word ring – we duplicate the four promises so the rotation reads
    // continuously without an empty arc.
    const promiseWords = t({
        de: "NACHHALTIG · REGENERATIV · ERFAHREN · PREMIUM · ",
        en: "SUSTAINABLE · RENEWABLE · EXPERIENCED · PREMIUM · ",
    });
    const ring = (promiseWords + promiseWords).trim();

    return (
        <div
            className="absolute z-20 pointer-events-none select-none
                       right-6 bottom-28 sm:right-10 sm:bottom-24
                       lg:right-16 lg:bottom-28
                       w-28 h-28 sm:w-36 sm:h-36 lg:w-44 lg:h-44"
            aria-hidden="true"
            data-testid="hero-quality-seal"
        >
            {/* outer rotating ring */}
            <svg
                viewBox="0 0 200 200"
                className="absolute inset-0 w-full h-full animate-spin-slow"
            >
                <defs>
                    <path
                        id="seal-ring"
                        d="M 100 100 m -82 0 a 82 82 0 1 1 164 0 a 82 82 0 1 1 -164 0"
                    />
                </defs>
                <text
                    fill="rgba(255,255,255,0.95)"
                    style={{
                        fontFamily: "Outfit, sans-serif",
                        fontSize: "13px",
                        letterSpacing: "0.32em",
                        fontWeight: 500,
                    }}
                >
                    <textPath href="#seal-ring" startOffset="0%">
                        {ring}
                    </textPath>
                </text>
            </svg>

            {/* static double border */}
            <svg
                viewBox="0 0 200 200"
                className="absolute inset-0 w-full h-full"
            >
                <circle
                    cx="100"
                    cy="100"
                    r="96"
                    fill="none"
                    stroke="rgba(212,183,154,0.85)"
                    strokeWidth="0.6"
                />
                <circle
                    cx="100"
                    cy="100"
                    r="68"
                    fill="none"
                    stroke="rgba(212,183,154,0.55)"
                    strokeWidth="0.6"
                />
                {/* tiny notches at 12/3/6/9 */}
                {[0, 90, 180, 270].map((deg) => (
                    <line
                        key={deg}
                        x1="100"
                        y1="4"
                        x2="100"
                        y2="12"
                        stroke="rgba(212,183,154,0.9)"
                        strokeWidth="1"
                        transform={`rotate(${deg} 100 100)`}
                    />
                ))}
            </svg>

            {/* center mark */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white leading-none">
                    <div
                        className="font-heading italic text-lg sm:text-2xl lg:text-3xl text-clay-300"
                        style={{ letterSpacing: "0.02em" }}
                    >
                        EH&nbsp;40
                    </div>
                    <div
                        className="font-body uppercase mt-1 text-[7px] sm:text-[8px] lg:text-[9px] tracking-[0.28em] text-white/80"
                    >
                        {t({ de: "Premium · Neubau", en: "Premium · New build" })}
                    </div>
                    <div className="mx-auto mt-1.5 w-4 sm:w-5 lg:w-6 h-px bg-clay-300/70" />
                    <div
                        className="font-body mt-1 text-[7px] sm:text-[8px] lg:text-[9px] tracking-[0.28em] text-clay-200/90"
                    >
                        2026
                    </div>
                </div>
            </div>
        </div>
    );
};
